<script lang="ts">
	import { CollisionPriority } from "@dnd-kit/abstract";
	import { DragDropProvider, KeyboardSensor, PointerSensor } from "@dnd-kit/svelte";
	import { isSortable } from "@dnd-kit/svelte/sortable";
	import deepEqual from "deep-equal";
	import { Dropdown, DropdownItem, Heading } from "flowbite-svelte";
	import { getContext } from "svelte";
	import { FieldPropertiesSchema, FieldSchema, FieldType, type Field, type FieldProperties } from "#gen/forms/v1/field_pb.js";
	import { FormSchema, type Form, type Rule } from "#gen/forms/v1/forms_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import type { DragEndEvent, DragOverEvent } from "#lib/components/flow/editor/types.js";
	import FieldContainer from "#lib/components/page/components/form/FieldContainer.svelte";
	import { fieldTypes } from "#lib/components/page/components/form/fieldTypes.js";
	import { generateId, removeFieldFromRules } from "#lib/components/page/components/form/utils.js";
	import { isFeatureEnabled } from "#lib/featureFlags.js";
	import type { JwtPayload } from "#lib/jwt.js";
	import FeatureFlagGate from "#lib/permissions/FeatureFlagGate.svelte";
	import { clone, create } from "#lib/protobuf-compat.js";
	import Button from "#lib/ui/Button/index.js";
	import type { Client } from "@optimizely/optimizely-sdk";
	import type { Readable } from "svelte/store";

	interface Props {
		form: Form;
		onInput?: (form: Form) => void;
		onChange?: (form: Form) => void;
	}

	const flags: Client = getContext("flags");
	const claims: Readable<JwtPayload> = getContext("claims");

	let { form, onInput, onChange }: Props = $props();

	let pendingScroll: Field | undefined = $state();
	let open = $state(false);

	let fields: Field[] = $derived(form.fields);
	let snapshot: Field[] = [];

	$effect(() => {
		if (!pendingScroll) {
			return;
		}
		const element = document.getElementById(`field-editor-${pendingScroll.name}`);
		element?.scrollIntoView({ behavior: "smooth", block: "start" });
		pendingScroll = undefined;
	});

	function selectElement(title: string, type: FieldType, properties?: FieldProperties) {
		addField(title, type, properties);
		open = false;
	}

	function addField(title: string, fieldType: FieldType, properties?: FieldProperties) {
		const name = generateId(form.fields, FieldType[fieldType].toLowerCase());
		const newField = create(FieldSchema, {
			name: name,
			title: title,
			fieldType: fieldType,
			properties: clone(FieldPropertiesSchema, properties),
			active: true,
			required: false,
		});
		const newFields = [...form.fields, newField];
		updateFields(newFields, form.rules);
		pendingScroll = newField;
	}

	function onDuplicate(fieldName: string) {
		const fieldIndex = form.fields.findIndex(field => field.name == fieldName);
		if (fieldIndex < 0) {
			return;
		}

		const newField = clone(FieldSchema, form.fields[fieldIndex]);
		newField.id = "";
		newField.immutable = false;
		newField.name = generateId(form.fields, newField.name);
		newField.modelFieldBinding = undefined;

		const newFields = [...form.fields.slice(0, fieldIndex + 1), newField, ...form.fields.slice(fieldIndex + 1)];
		updateFields(newFields, form.rules);
		pendingScroll = newField;
	}

	function onRemove(fieldName: string) {
		const fieldIndex = form.fields.findIndex(field => field.name == fieldName);
		if (fieldIndex < 0) {
			return;
		}

		const newFields = form.fields.filter((_, i) => i !== fieldIndex);
		const newRules = removeFieldFromRules(form.rules, fieldName);
		updateFields(newFields, newRules);
	}

	function onDragStart() {
		snapshot = fields.slice();
	}

	function onDragOver(event: DragOverEvent) {
		const { source, target } = event.operation;
		if (isSortable(source) && isSortable(target)) {
			const fromIndex = source.index;
			const toIndex = target.index;
			if (fromIndex !== toIndex) {
				const newFields = [...fields];
				const [removed] = newFields.splice(fromIndex, 1);
				newFields.splice(toIndex, 0, removed);
				fields = newFields;
			}
		}
	}

	function onDragEnd(event: DragEndEvent) {
		if (event.canceled || deepEqual(fields, snapshot)) {
			fields = snapshot;
			return;
		}
		updateFields(fields, form.rules);
	}

	// Always build a new form object to ensure the form reference changes and triggers reactivity in parent components
	function buildNextForm(fields: Field[], rules: Rule[]): Form {
		const next = clone(FormSchema, form);
		next.fields = fields;
		next.rules = rules;
		return next;
	}

	function updateFields(fields: Field[], rules: Rule[]) {
		onChange?.(buildNextForm(fields, rules));
	}

	function handleOnInput(field: Field) {
		const fieldIndex = form.fields.findIndex(f => f.name == field.name);
		if (fieldIndex < 0) {
			return;
		}
		const newFields = form.fields.map((f, i) => (i === fieldIndex ? field : f));
		onInput?.(buildNextForm(newFields, form.rules));
	}

	function handleOnChange(field: Field) {
		const fieldIndex = form.fields.findIndex(f => f.name == field.name);
		if (fieldIndex < 0) {
			return;
		}
		const newFields = form.fields.map((f, i) => (i === fieldIndex ? field : f));
		onChange?.(buildNextForm(newFields, form.rules));
	}
</script>

<div>
	<div class="sticky top-0 z-10 flex w-full items-center justify-between gap-4 bg-white py-4">
		<FeatureFlagGate flag="build_your_own_page">
			<P class="grow text-sm font-bold">Form fields</P>
			{#snippet fallback()}
				<Heading tag="h2" class="grow text-lg font-semibold">Form fields</Heading>
			{/snippet}
		</FeatureFlagGate>
		<div class="flex-none">
			<Button id="add-field-button" color="alternative" data-action-name="form-designer-add-field" data-action-title="Add field">
				<Icon name="plus" class="mr-2" />
				Add field
			</Button>
		</div>
	</div>
	<Dropdown triggeredBy="#add-field-button" bind:open placement="bottom-end" class="w-145 rounded-lg border border-gray-300">
		<div class="grid grid-cols-3 gap-4 p-4">
			{#each fieldTypes as section (section.title)}
				<div>
					<Heading tag="h3" color="text-gray-500" class="p-3 text-xs font-normal uppercase">{section.title}</Heading>
					{#each section.elements as element (element.title)}
						{#if !element.requiredFeatureFlag || isFeatureEnabled(flags, element.requiredFeatureFlag, $claims)}
							<DropdownItem class="px-2 py-2 hover:rounded-lg" on:click={() => selectElement(element.title, element.fieldType, element.fieldProperties)} data-action-name="form-designer-add-field-type" data-action-title={element.title}>
								<div class="flex items-center gap-2">
									<div class="rounded-lg p-2 text-center" style="background-color: {section.accentColor};">
										<Icon name={element.icon} iconSet={element.iconSet} class="m-auto h-5 w-5" />
									</div>
									<P size="sm" weight="medium">{element.title}</P>
								</div>
							</DropdownItem>
						{/if}
					{/each}
				</div>
			{/each}
		</div>
	</Dropdown>
	<DragDropProvider sensors={[KeyboardSensor, PointerSensor]} {onDragStart} {onDragOver} {onDragEnd}>
		<FieldContainer id={form.id} collisionPriority={CollisionPriority.Low} {form} {fields} onInput={handleOnInput} onChange={handleOnChange} {onRemove} {onDuplicate} />
	</DragDropProvider>
</div>
